import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { n0, fmtDate, fmtDayMonth } from './format';

/* Графики рисуются инлайновым SVG в реальных пикселях контейнера: подписи осей
   не растягиваются вместе с картинкой. Библиотека для этого не нужна. */

const esc = (s) =>
  String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function areaSvg(data, w, { h, valueKey, color, fmt, labels }) {
  const pad = { t: 12, r: 4, b: labels ? 20 : 4, l: 4 };
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const X = (i) => pad.l + (data.length < 2 ? iw / 2 : (i / (data.length - 1)) * iw);
  const Y = (v) => pad.t + ih - (v / max) * ih;
  const line = data.map((d, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(d[valueKey]).toFixed(1)).join(' ');
  const fill = `${line} L${X(data.length - 1).toFixed(1)} ${pad.t + ih} L${X(0).toFixed(1)} ${pad.t + ih} Z`;
  const gid = 'g' + Math.random().toString(36).slice(2, 7);

  let grid = '';
  let ticks = '';
  for (let g = 0; g <= 2; g++) {
    const y = pad.t + ih - (g / 2) * ih;
    grid += `<line class="gl" x1="${pad.l}" y1="${y}" x2="${w - pad.r}" y2="${y}"/>`;
    ticks += `<text x="${pad.l + 3}" y="${y - 4}">${esc(fmt((max * g) / 2))}</text>`;
  }

  let xlab = '';
  if (labels) {
    const step = Math.max(1, Math.floor(data.length / 6));
    for (let i = 0; i < data.length; i += step)
      xlab += `<text x="${X(i)}" y="${h - 5}" text-anchor="middle">${esc(fmtDayMonth(data[i].date))}</text>`;
  }

  const hit = data
    .map(
      (d, i) =>
        `<rect x="${(X(i) - iw / data.length / 2).toFixed(1)}" y="0" width="${(iw / data.length).toFixed(1)}"
          height="${h}" fill="transparent" data-tip="${esc(fmtDate(d.date))} · <b>${esc(fmt(d[valueKey]))}</b>"/>`
    )
    .join('');

  return `<svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" style="display:block">
    <defs><linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${color}" stop-opacity=".34"/>
      <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
    </linearGradient></defs>
    ${grid}<path d="${fill}" fill="url(#${gid})"/>
    <path d="${line}" fill="none" stroke="${color}" stroke-width="1.8"/>
    ${ticks}${xlab}${hit}</svg>`;
}

function barsSvg(data, w, { h, valueKey, color, fmt }) {
  const pad = { t: 12, r: 4, b: 20, l: 4 };
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const bw = iw / data.length;

  let grid = '';
  let ticks = '';
  for (let g = 0; g <= 2; g++) {
    const y = pad.t + ih - (g / 2) * ih;
    grid += `<line class="gl" x1="${pad.l}" y1="${y}" x2="${w - pad.r}" y2="${y}"/>`;
    ticks += `<text x="${pad.l + 3}" y="${y - 4}">${esc(fmt((max * g) / 2))}</text>`;
  }

  const rects = data
    .map((d, i) => {
      const bh = (d[valueKey] / max) * ih;
      const label = d.label || fmtDate(d.date);
      return `<rect x="${(pad.l + i * bw + bw * 0.14).toFixed(1)}" y="${(pad.t + ih - bh).toFixed(1)}"
        width="${(bw * 0.72).toFixed(1)}" height="${Math.max(1, bh).toFixed(1)}" rx="1.5" fill="${color}"
        data-tip="${esc(label)} · <b>${esc(fmt(d[valueKey]))}</b>"/>`;
    })
    .join('');

  const step = Math.max(1, Math.floor(data.length / 8));
  let xlab = '';
  for (let i = 0; i < data.length; i += step)
    xlab += `<text x="${pad.l + i * bw + bw / 2}" y="${h - 5}" text-anchor="middle">${esc(
      data[i].label || fmtDayMonth(data[i].date)
    )}</text>`;

  return `<svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" style="display:block">
    ${grid}${rects}${ticks}${xlab}</svg>`;
}

/* Общая обёртка: меряет ширину, перерисовывает при ресайзе, показывает подсказку. */
function Svg({ height, draw }) {
  const box = useRef(null);
  const [w, setW] = useState(0);

  useLayoutEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    const measure = () => setW(Math.max(220, Math.round(node.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (box.current && w) box.current.innerHTML = draw(w);
  }, [w, draw]);

  return <div className="chart-wrap" ref={box} style={{ height }} onMouseMove={showTip} onMouseLeave={hideTip} />;
}

export function AreaChart({ data, height = 150, valueKey = 'v', color = '#9a6aff', fmt = n0, labels = true }) {
  const draw = (w) => areaSvg(data, w, { h: height, valueKey, color, fmt, labels });
  return <Svg height={height} draw={draw} />;
}

export function BarChart({ data, height = 150, valueKey = 'v', color = '#427fff', fmt = n0 }) {
  const draw = (w) => barsSvg(data, w, { h: height, valueKey, color, fmt });
  return <Svg height={height} draw={draw} />;
}

export function Donut({ parts, size = 132, thick = 15 }) {
  const total = parts.reduce((a, p) => a + p.v, 0) || 1;
  const r = size / 2 - thick / 2;
  const C = 2 * Math.PI * r;
  let off = 0;

  return (
    <div className="chart-wrap donut" onMouseMove={showTip} onMouseLeave={hideTip}>
      <svg width={size} height={size} style={{ flex: 'none' }}>
        {parts.map((p) => {
          const len = (p.v / total) * C;
          const seg = (
            <circle
              key={p.l}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={p.c}
              strokeWidth={thick}
              strokeDasharray={`${len.toFixed(2)} ${(C - len).toFixed(2)}`}
              strokeDashoffset={(-off).toFixed(2)}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              data-tip={`${esc(p.l)} · <b>${n0(p.v)}</b> (${((p.v / total) * 100).toFixed(0)}%)`}
            />
          );
          off += len;
          return seg;
        })}
        <text x={size / 2} y={size / 2 - 2} textAnchor="middle" className="donut-total">
          {n0(total)}
        </text>
        <text x={size / 2} y={size / 2 + 14} textAnchor="middle" className="donut-cap">
          всего
        </text>
      </svg>
      <div className="donut-legend">
        {parts.map((p) => (
          <div className="row" key={p.l}>
            <i style={{ background: p.c }} />
            <span className="rn">{p.l}</span>
            <span className="rv">
              {n0(p.v)} · {((p.v / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Одна подсказка на всё приложение — она живёт в App.jsx. */
function showTip(e) {
  const tip = document.getElementById('tip');
  if (!tip) return;
  const target = e.target.closest('[data-tip]');
  if (!target) {
    tip.style.opacity = 0;
    return;
  }
  tip.innerHTML = target.dataset.tip;
  tip.style.opacity = 1;
  tip.style.left = Math.min(e.clientX + 14, window.innerWidth - tip.offsetWidth - 10) + 'px';
  tip.style.top = e.clientY - tip.offsetHeight - 12 + 'px';
}
function hideTip() {
  const tip = document.getElementById('tip');
  if (tip) tip.style.opacity = 0;
}
